'use client'

import {
  type ModalProps as ModalUnstyledProps,
  type SlotComponentProps,
  useSlotProps,
} from '@mui/base'
import { clsx } from 'clsx'
import { type ComponentProps, type ElementType, type JSX } from 'react'

import { IconClose } from '@redteclab/icons'

import { useLocaleContext } from '../LocaleContext'

export type DialogTitleProps = ComponentProps<'div'> & {
  onClose?: ModalUnstyledProps['onClose']
  slotProps?: {
    closeButton?: SlotComponentProps<'button', NonNullable<unknown>, undefined>
    heading?: SlotComponentProps<'h3', NonNullable<unknown>, undefined>
  }
  slots?: {
    closeButton?: ElementType
  }
}

export const DialogTitle = (props: DialogTitleProps): JSX.Element => {
  const { children, className, onClose, slotProps, slots, ...rest } = props

  const closeLabel = useLocaleContext().dialog.close

  const CloseButton = slots?.closeButton ?? 'button'
  const closeButtonProps = useSlotProps({
    additionalProps: {
      'aria-label': closeLabel,
      onClick: (): void => {
        onClose?.({}, 'escapeKeyDown')
      },
      type: 'button',
    },
    className: 'dialog-title__close-button',
    elementType: CloseButton,
    externalSlotProps: slotProps?.closeButton,
    ownerState: undefined,
  })
  const headingProps = useSlotProps({
    className: 'dialog-title__text',
    elementType: 'h3',
    externalSlotProps: slotProps?.heading,
    ownerState: undefined,
  })

  return (
    <div {...rest} className={clsx('dialog-title', className)}>
      <h3 {...headingProps}>{children}</h3>
      <CloseButton {...closeButtonProps}>
        <IconClose aria-hidden className="dialog-title__close-button-icon" />
      </CloseButton>
    </div>
  )
}
